import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DEFAULT_LIMIT, DEFAULT_OFFSET, LINKS } from '@trip-ux/constants';
import { AgentSearchFilter } from '../user-info';
import { RootStateOrAny } from 'react-redux';
import { isEmpty, pickBy, trim, get } from 'lodash';
import { apiClient } from '../api.client';
import {
  AgentSearchResponse,
  AgentSearchResult,
  AgentSlice,
  AGENT_TABLE_LENGTH_PER_PAGE,
  Agent,
  AgentRoles,
  Roles
} from './types';
import axios from 'axios';

const initialState: AgentSlice = {
  isAgentFailure: false,
  isAgentLoading: false,
  isAgentSuccess: false,

  searchFilter: {},
  sortField: undefined,
  sortOrder: 'asc',
  searchResultRaw: [],

  searchResult: [],
  searchResultTotalCount: 0,
  currentPage: 1,
  totalPages: 1,

  isRolesLoading: false,
  isRolesSuccess: false,
  isRolesFailure: false,
  roles: []
};

export const formatFetchAgentsFilter = (form: Record<string, any>): AgentSearchFilter => ({
  firstName: trim(form.firstName) || '',
  lastName: trim(form.lastName) || '',
  email: trim(form.emailAddress) || '',
  profileCode: trim(form.profileCode) || '',
  status: trim(form.agentStatus) || '',
  title: trim(form.title) || '',
  role: trim(form.role) || '',
  organization: trim(form.organization) || ''
});

const getTranslate = (item: string, labels: any, type: string) =>
  get(labels, type, []).find(option => option.value === item)?.label;

const formatAgentSearchResponse = (
  agents: AgentSearchResult[],
  labels: any
): (Partial<Agent> | Record<string, string | number>)[] => {
  return agents.map(agent => ({
    agentName: agent.firstName + ' ' + agent.lastName,
    profileCode: agent.profileCode,
    profileRef: agent.profileRef,
    status: getTranslate(agent.status, labels, 'misc.statusOptions'),
    role: getTranslate(agent.title, labels, 'misc.titleOptions'),
    accessType: getTranslate(agent.role, labels, 'misc.roleOptions')
  }));
};

export const fetchRoles = createAsyncThunk(LINKS.GET_ROLES, async () => {
  return axios.get(LINKS.GET_ROLES);
});

export const fetchAgents = createAsyncThunk(
  LINKS.AGENTS_SEARCH,
  async (
    payload: {
      filter?: AgentSearchFilter;
      page?: number;
      sortOrder?: 'asc' | 'desc';
      sortField?: string;
      isNewSearch?: boolean;
      labels?: any;
    },
    { getState, dispatch }: { getState: () => RootStateOrAny; dispatch }
  ) => {
    const { sortField, sortOrder, currentPage, searchFilter } = payload.isNewSearch ? initialState : getState()?.agent;
    const targetPage = payload.page || currentPage;
    const searchOffset = Math.max(DEFAULT_OFFSET, (targetPage - 1) * AGENT_TABLE_LENGTH_PER_PAGE);
    const filter = isEmpty(payload.filter) ? searchFilter : payload.filter;
    // Filter out empty fields
    const filteredParams = pickBy(
      {
        ...filter,
        sortField: payload.sortField || sortField,
        sortOrder: payload.sortOrder || sortOrder,
        offset: searchOffset,
        limit: DEFAULT_LIMIT
      },
      value => value !== '' && value !== undefined
    );

    return apiClient(dispatch, getState).get(LINKS.AGENTS_SEARCH, {
      params: filteredParams
    });
  }
);

export const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    resetAgentState: () => {
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAgents.pending, (state, action) => {
        if (action?.meta?.arg?.filter) {
          state.searchFilter = action?.meta?.arg?.filter;
        }

        state.sortField = action?.meta?.arg?.sortField || state.sortField;
        state.sortOrder = action?.meta?.arg?.sortOrder || 'asc';

        state.isAgentLoading = true;
        state.isAgentSuccess = false;
        state.isAgentFailure = false;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        const { isNewSearch, page, labels } = action?.meta?.arg || {};
        const totalCount = (action?.payload as unknown as AgentSearchResponse)?.total || 0;
        const fetchedAgents: AgentSearchResult[] = (action?.payload as unknown as AgentSearchResponse)?.agents;

        const formattedAgents = formatAgentSearchResponse(fetchedAgents, labels);

        state.searchResultRaw = fetchedAgents;
        state.searchResult = formattedAgents;
        state.searchResultTotalCount = totalCount;
        state.totalPages = Math.ceil(totalCount / AGENT_TABLE_LENGTH_PER_PAGE);
        state.currentPage = isNewSearch ? 1 : page || state.currentPage;

        state.isAgentLoading = false;
        state.isAgentSuccess = true;
        state.isAgentFailure = false;
      })
      .addCase(fetchAgents.rejected, state => {
        state.searchResult = [];
        state.searchResultTotalCount = 0;
        state.totalPages = 1;
        state.currentPage = 1;
        state.sortField = null;

        state.isAgentLoading = false;
        state.isAgentSuccess = false;
        state.isAgentFailure = true;
      })
      .addCase(fetchRoles.pending, state => {
        state.isRolesLoading = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isRolesLoading = false;
        state.isRolesSuccess = true;
        const fetchedRoles: Roles[] = (action?.payload as unknown as AgentRoles)?.data;
        //TO FIX: this should be part of the api to accept a category as input and check permissions to allow retrieval of Internal roles
        const externalAgentRoles = fetchedRoles.filter(role => role.category === 'Agency');
        state.roles = externalAgentRoles;
      })
      .addCase(fetchRoles.rejected, state => {
        state.isRolesLoading = false;
        state.isRolesFailure = true;
      });
  }
});

export const selectAgentLoading = state => state.agent.isAgentLoading;
export const selectAgentSuccess = state => state.agent.isAgentSuccess;
export const selectAgentFailure = state => state.agent.isAgentFailure;
export const selectAgentSearchFilter = state => state.agent.searchFilter;
export const selectAgentSearchResult = state => state.agent.searchResult;
export const selectAgentSearchResultRaw = state => state.agent.searchResultRaw;
export const selectAgentSearchResultTotalCount = state => state.agent.searchResultTotalCount;
export const selectAgentCurrentPage = state => state.agent.currentPage;
export const selectAgentTotalPages = state => state.agent.totalPages;
export const selectAgentSortOrder = state => state.agent.sortOrder;
export const selectAgentSortField = state => state.agent.sortField;

export const selectRoleLoading = state => state.agent.isRolesLoading;
export const selectRoleSuccess = state => state.agent.isRolesSuccess;
export const selectRoleFailure = state => state.agent.isRolesFailure;
export const selectRole = state => state.agent.roles;

export const { resetAgentState } = agentSlice.actions;
export default agentSlice.reducer;
